import React, { useCallback, useEffect, useMemo } from 'react'
import {
  Typography, Grid, Checkbox, Input, Select, MenuItem, FormControlLabel,
  ThemeProvider, FilledInput, RadioGroup, Radio
} from '@material-ui/core'
import { ToggleButton } from '@material-ui/lab'
import { makeStyles, withStyles, darken } from '@material-ui/core/styles'

import { DatePicker, TimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers'
import MomentUtils from '@date-io/moment'
import moment from 'moment'
import { pickerTheme } from '../../../../utils/variables'

const FilterSubscription = props => {
  const { subOptions, setSubOptions, subEndSettings, setSubEndSettings, isEditing, subErrCheck, subErrs, subEndObj, subDateErrCheck, resetDatesAndTime = () => {} } = props

  const weekdayInfo = [
    { title: 'S', value: 0 },
    { title: 'M', value: 1 },
    { title: 'T', value: 2 },
    { title: 'W', value: 3 },
    { title: 'T', value: 4 },
    { title: 'F', value: 5 },
    { title: 'S', value: 6 }
  ]

  const useStyles = makeStyles((theme) => ({
    weekdayBtn: {
      color: '#98B556',
      border: 'none',
      borderRadius: '50%',
      backgroundColor: '#E5ECD4',
      minWidth: 0,
      width: theme.spacing(4),
      height: theme.spacing(4),
      marginRight: '0.5rem',
      '&.Mui-selected': {
        backgroundColor: '#98B556',
        color: 'white',
        outline: 'none',
        '&:hover': {
          backgroundColor: darken('#98B556', 0.2),
          color: '#FFF'
        }
      },
      '&:hover': {
        backgroundColor: darken('#E5ECD4', 0.2),
        color: '#FFF'
      }
    },
    subscribeText: {
      color: '#6A6A6A',
      fontWeight: 600
    },
    occurancesInput: {
      '& .MuiFilledInput': {
        input: {
          '& .MuiInputBase': {
            input: {
              padding: theme.spacing(1)
            }
          }
        }
      }
    }
  }))

  const classes = useStyles()

  const GreenCheckbox = withStyles({
    root: {
      color: '#595959',
      '&$checked': {
        color: '#A4D164'
      }
    },
    checked: {}
  })((props) => <Checkbox color='default' {...props} />)

  // Checks for errors to determine which error text to return. If there are no errors, return false.

  const handleSubErrTextDisplay = () => {
    const { errorOne, errorTwo, errorThree, errorFour } = subErrs

    if (errorOne) { return 'Please provide a valid frequency and day *' }
    if (errorTwo || errorFour) { return 'Please provide a valid frequency *' }
    if (errorThree) { return 'Please provide a valid day *' }

    return false
  }

  const startDateCheck = (date) => {
    if (subDateErrCheck) { return false }

    const invalidStartDateCheck = isEditing && moment(date).isBefore(moment().subtract(1, 'day'))
    return invalidStartDateCheck
  }
  const endDateCheck = (date) => {
    if (subDateErrCheck) { return false }

    const invalidEndDateCheck = moment(date).isBefore(subOptions.subStartDate)
    return invalidEndDateCheck
  }

  const handleDateChange = (startDate, endDate) => {
    if (startDate) {
      // validate start date
      if (!startDateCheck(startDate)) {
        setSubOptions({ ...subOptions, subStartDate: startDate })
      }
    }

    if (endDate) {
      if (!endDateCheck(endDate)) {
        setSubOptions({ ...subOptions, subEndDate: endDate })
      }
    }
  }

  const handleFrequencyChange = (e) => {
    if (e.target.value && parseInt(e.target.value) != null) {
      if (parseInt(e.target.value) > 0) { setSubOptions({ ...subOptions, subFrequency: e.target.value }) }
    } else {
      setSubOptions({ ...subOptions, subFrequency: e.target.value })
    }
  }

  const handleSubEndChange = (e) => {
    if (e.target.value === subEndObj.never) {
      setSubEndSettings(subEndObj.never)
      setSubOptions({ ...subOptions, subEndDate: null, subEndLimit: 0 })
    } else if (e.target.value === subEndObj.on) {
      setSubEndSettings(subEndObj.on)
      setSubOptions({ ...subOptions, subEndLimit: 0 })
    } else if (e.target.value === subEndObj.after) {
      setSubEndSettings(subEndObj.after)
      setSubOptions({ ...subOptions, subEndDate: null })
    }
  }

  const handleSubStartTimeChange = (startTime) => {
    setSubOptions({ ...subOptions, subStartTime: startTime })
  }

  // manually prevents user from entering conflicting data for ending subscription
  useMemo(() => {
    // temp var for first time load-in of existing filters (process of elimination to grab val for subEndSettings RadioGroup)
    let tempSubEndSettings = ''
    if (isEditing) {
      if (!subEndSettings) {
        if (subOptions.subEndLimit && (!subOptions.subEndDate || !moment.isMoment(subOptions.subEndDate))) {
          tempSubEndSettings = subEndObj.after
        } else if ((subOptions.subEndDate && moment.isMoment(subOptions.subEndDate)) && !subOptions.subEndLimit) {
          tempSubEndSettings = subEndObj.on
        } else if ((!subOptions.subEndDate || !moment.isMoment(subOptions.subEndDate)) && (!subOptions.subEndLimit || subOptions.subEndLimit === 0)) {
          tempSubEndSettings = subEndObj.never
        }

        if (tempSubEndSettings !== subEndSettings) {
          setSubEndSettings(tempSubEndSettings)
        }
      }
    }
  }, [subEndObj, isEditing, subOptions.subEndLimit, subOptions.subEndDate, subEndSettings, setSubEndSettings])

  const handleCleanup = useCallback(() => {
    setSubOptions({
      subEnabled: false,
      subFrequency: 0,
      subFrequencyType: 'W',
      subStartDate: null,
      subSendEmailOn: null,
      subEndDate: null,
      subEndLimit: 0,
      subFormat: 'PDF'
    })
    setSubEndSettings('')
  }, [setSubOptions, setSubEndSettings])

  useEffect(() => {
    return () => {
      handleCleanup()
    }
  }, [handleCleanup])

  const handleToggleScheduled = () => {
    // if currently false, then we are toggling the scheduled filter to 'on' and we need to reset the dates in the filter
    if (!subOptions.subEnabled) {
      resetDatesAndTime()
    }

    setSubOptions({
      ...subOptions, subEnabled: !subOptions.subEnabled
    })
  }

  return (
    <>
      <Grid item container direction='column'>
        <Grid item container direction='row' alignItems='center'>
          <GreenCheckbox
            checked={subOptions.subEnabled}
            onChange={() => handleToggleScheduled()}
          />
          <Typography style={{ color: '#6A6A6A', fontWeight: 600, fontSize: '1.1rem' }} className='elevio-subscribe-to-filter'>Subscribe to Filter? {!subOptions.subEnabled && '**'}</Typography>
        </Grid>
        <Grid item container direction='column'>
          <Typography style={{ fontSize: '12px', marginLeft: '2em' }}>Select this option to schedule a regular delivery time of your saved filter.</Typography>
          {
            !subOptions.subEnabled &&
            (
              <Typography variant='caption' style={{ fontSize: '11px', marginLeft: '2em', color: '#AFAFAF' }}>** Subscribing to a filter will override any date and time currently selected.</Typography>
            )
          }
        </Grid>
      </Grid>
      {
        subOptions.subEnabled && (
          <>
            <Grid item container direction='row' style={{ backgroundColor: '#E0E0E040' }}>
              <Grid item>

                <Grid container direction='column' style={{ padding: '1em', marginBottom: '1.2em' }}>

                  <Grid item>
                    <Typography className={classes.subscribeText}>Send filter every:</Typography>
                  </Grid>

                  <Grid container direction='row' style={{ alignContent: 'center', padding: '0.5em 0' }}>
                    <Grid item style={{ marginRight: '0.8em' }}>
                      <Input
                        type='number'
                        disableUnderline
                        value={subOptions.subFrequency}
                        onChange={handleFrequencyChange}
                        style={{
                          backgroundColor: '#F3EEEE',
                          borderRadius: '5px',
                          maxWidth: '3em',
                          padding: '2px',
                          marginRight: '1em',
                          border: !subOptions.subFrequency ? '2px solid red' : null
                        }}
                      />
                      <Select
                        disableUnderline
                        value={subOptions.subFrequencyType}
                        onChange={(e) => setSubOptions({ ...subOptions, subFrequencyType: e.target.value })}
                        style={{
                          fontSize: '13px',
                          padding: '4px',
                          backgroundColor: '#F3EEEE',
                          color: '#6A6A6A',
                          borderRadius: '5px'
                        }}
                      >
                        <MenuItem value='H'>Hours</MenuItem>
                        <MenuItem value='D'>Days </MenuItem>
                        <MenuItem value='W'>Weeks</MenuItem>
                        <MenuItem value='M'>Months</MenuItem>
                      </Select>
                    </Grid>
                  </Grid>

                  {subErrCheck &&
                    (
                      <>
                        <Grid container direction='column' style={{ marginBottom: '0.7em', wordBreak: 'break-word' }}>
                          <Typography variant='caption' color='error'>{handleSubErrTextDisplay()}</Typography>
                        </Grid>
                      </>
                    )}

                  <Grid container direction='column'>
                    <Grid item>
                      <Typography className={classes.subscribeText} gutterBottom>Starting on:</Typography>
                    </Grid>
                    <ThemeProvider theme={pickerTheme}>
                      <MuiPickersUtilsProvider utils={MomentUtils}>
                        <Grid item>
                          <DatePicker
                            autoOk
                            label='Start Date *'
                            variant='inline'
                            inputVariant='standard'
                            placeholder='--/--/----'
                            format='MM/DD/YYYY'
                            value={subOptions.subStartDate}
                            style={{ '.MuiInputBase': { backgroundColor: '' } }}
                            onChange={date => handleDateChange(date, null)}
                            disablePast={!isEditing}
                            error={!subOptions.subStartDate}
                          />
                        </Grid>
                      </MuiPickersUtilsProvider>
                    </ThemeProvider>
                  </Grid>

                  <Grid container direction='column'>
                    <MuiPickersUtilsProvider utils={MomentUtils}>
                      <Grid item style={{ marginTop: '1.2em' }}>
                        <TimePicker
                          label='Send Time'
                          clearable
                          minutesStep={15}
                          value={subOptions.subStartTime}
                          onChange={(e) => handleSubStartTimeChange(e)}
                        />
                      </Grid>
                    </MuiPickersUtilsProvider>
                  </Grid>

                  <Grid container direction='column' style={{ marginTop: '1.2em' }}>
                    <Grid item>
                      <Typography className={classes.subscribeText} gutterBottom>Select Format:</Typography>
                    </Grid>
                    <Grid item>
                      <Select
                        disableUnderline
                        value={subOptions.subFormat}
                        onChange={(e) => setSubOptions({ ...subOptions, subFormat: e.target.value })}
                        style={{
                          fontSize: '13px',
                          padding: '4px',
                          backgroundColor: '#F3EEEE',
                          color: '#6A6A6A',
                          borderRadius: '5px',
                          maxWidth: 'fit-content'
                        }}
                      >
                        <MenuItem value='CSV'>CSV</MenuItem>
                        <MenuItem value='PDF'>PDF</MenuItem>
                      </Select>
                    </Grid>
                  </Grid>

                </Grid>
              </Grid>
              <Grid item container xs>
                {
                  subOptions.subFrequencyType === 'W' && (
                    <Grid item container direction='column' style={{ padding: '1em' }}>
                      <Grid item>
                        <Typography className={classes.subscribeText} gutterBottom>
                          Send Email on:
                        </Typography>
                      </Grid>
                      <Grid item container direction='row' style={{ marginBottom: '1.2em' }}>
                        {
                          weekdayInfo.map((day, id) =>
                            <Grid item key={id}>
                              <ToggleButton
                                className={classes.weekdayBtn}
                                selected={Boolean(subOptions.subSendEmailOn != null && subOptions.subSendEmailOn === day.value)}
                                value={day.value}
                                onChange={() => setSubOptions({ ...subOptions, subSendEmailOn: day.value })}
                                disableRipple
                              >
                                <Typography variant='caption'>{day.title}</Typography>
                              </ToggleButton>
                            </Grid>
                          )
                        }
                      </Grid>
                    </Grid>
                  )
                }

                <Grid item container direction='column' align='center'>
                  <Grid item>
                    <Typography className={classes.subscribeText} justify='flex-start'>Ends:</Typography>
                  </Grid>

                  <RadioGroup value={subEndSettings} onChange={handleSubEndChange}>
                    <FormControlLabel value={subEndObj.never} control={<Radio checked={subEndSettings === subEndObj.never} />} label='Never' />
                    <Grid item container direction='row'>
                      <Grid item>
                        <FormControlLabel value={subEndObj.on} control={<Radio checked={subEndSettings === subEndObj.on} />} label='On' />
                      </Grid>
                      <ThemeProvider theme={pickerTheme}>
                        <MuiPickersUtilsProvider utils={MomentUtils}>
                          <Grid item xs style={{ textAlign: 'flex-start', padding: '1em' }}>
                            <DatePicker
                              autoOk
                              variant='inline'
                              inputVariant='standard'
                              placeholder='--/--/----'
                              format='MM/DD/YYYY'
                              disabled={subEndSettings !== 'on' || !subOptions.subStartDate}
                              value={subOptions.subEndDate}
                              onChange={(date) => handleDateChange(null, date)}
                              disablePast={subEndSettings === subEndObj.on && !isEditing}
                              error={subDateErrCheck}
                              minDate={moment(subOptions.subStartDate).add(1, 'day')}
                              minDateMessage='End date cannot occur before the start date *'
                            />
                          </Grid>
                        </MuiPickersUtilsProvider>
                      </ThemeProvider>
                    </Grid>

                    <Grid item container direction='row'>
                      <Grid item>
                        <FormControlLabel value='after' control={<Radio checked={subEndSettings === subEndObj.after} />} label='After' />
                      </Grid>

                      <Grid item xs style={{ paddingRight: '1em' }}>
                        <FilledInput
                          disabled={Boolean(subOptions.subEndDate) || subEndSettings !== 'after'}
                          inputProps={{ style: { padding: '0.5em 1em' }, value: subOptions.subEndLimit ? subOptions.subEndLimit : 0 }}
                          className={classes.occurancesInput}
                          type='number'
                          min={1}
                          fullWidth
                          disableUnderline
                          style={{ borderRadius: '5px', alignItems: 'baseline' }}
                          endAdornment={<Typography style={{ fontSize: '13px' }}>occurances</Typography>}
                          onChange={e => { e.target.value > 0 ? setSubOptions({ ...subOptions, subEndLimit: e.target.value }) : e.target.value = 1 }}
                        />
                      </Grid>
                    </Grid>
                  </RadioGroup>
                </Grid>
              </Grid>
            </Grid>
          </>
        )
      }
    </>
  )
}

export default FilterSubscription
